import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Play, Video, ExternalLink } from 'lucide-react';
import type { CalendarEvent } from '../types/calendar.types.ts';
import {
  buildPlanMeetingUrl,
  formatDayLabel,
  formatTimeRange,
  isSameDay,
} from '../utils/calendarUtils.ts';

const COLOR_DOT: Record<CalendarEvent['color'], string> = {
  purple: 'bg-purple-500',
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  gray: 'bg-slate-500',
};

type CalendarDayPanelProps = {
  selectedDate: Date;
  events: CalendarEvent[];
};

const CalendarDayPanel: React.FC<CalendarDayPanelProps> = ({ selectedDate, events }) => {
  const dayEvents = events.filter((e) => isSameDay(e.start, selectedDate));

  return (
    <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4">
      <h3 className="text-white font-semibold text-sm mb-1">Seçili gün</h3>
      <p className="text-gray-400 text-xs mb-4 capitalize">{formatDayLabel(selectedDate)}</p>

      {dayEvents.length === 0 ? (
        <p className="text-gray-500 text-sm py-6 text-center">Bu günde etkinlik yok</p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {dayEvents.map((event) => (
            <div
              key={event.id}
              className="rounded-lg border border-white/10 bg-slate-900/40 p-3"
            >
              <div className="flex items-start gap-2">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${COLOR_DOT[event.color]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{event.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5">
                    {formatTimeRange(event.start, event.end)}
                  </p>
                  {event.source === 'google' && (
                    <span className="inline-flex items-center gap-1 text-blue-400 text-[10px] mt-1">
                      <ExternalLink className="w-3 h-3" />
                      Google Takvim
                    </span>
                  )}
                  {event.meeting?.meetingFormat === 'IN_PERSON' && event.location && (
                    <p className="text-green-400/80 text-xs mt-1 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {event.location}
                    </p>
                  )}
                </div>
              </div>

              {event.meeting && event.meeting.meetingFormat !== 'IN_PERSON' && (
                <Link
                  to={`/meeting/${event.meeting.meetingCode}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-purple-300 hover:text-purple-200"
                >
                  <Play className="w-3 h-3" />
                  Toplantıya git
                </Link>
              )}
              {event.meeting && (
                <Link
                  to="/plans"
                  className="mt-1 block text-xs text-gray-500 hover:text-gray-300"
                >
                  Detaylar →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      <Link
        to={buildPlanMeetingUrl(selectedDate)}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition"
      >
        <Video className="w-4 h-4" />
        Toplantı planla
      </Link>
    </div>
  );
};

export default CalendarDayPanel;
